import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PLACEMENTS = [
  { value: "home_top", label: "Homepage (banner in alto)" },
  { value: "article_inline", label: "Pagina articolo (nel testo)" },
  { value: "archive_top", label: "Archivio (banner in alto)" },
  { value: "sidebar", label: "Colonna laterale" },
];

const EMPTY = { title: "", sponsor: "", image_url: "", link_url: "", placement: "home_top", active: true };

export default function AdManager() {
  const [ads, setAds] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => setAds([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (ad) => { setForm({ ...EMPTY, ...ad }); setEditId(ad.id); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/admin/ads/${editId}`, form);
      else await api.post("/admin/ads", form);
      toast.success(editId ? "Banner aggiornato" : "Banner creato");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (ad) => {
    await api.put(`/admin/ads/${ad.id}`, { ...ad, active: !ad.active });
    load();
  };

  const del = async (id) => {
    await api.delete(`/admin/ads/${id}`);
    toast.success("Banner eliminato");
    setAds((prev) => prev.filter((a) => a.id !== id));
  };

  if (ads === null) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Gestisci banner e contenuti sponsorizzati mostrati sul sito.</p>
        <Button size="sm" onClick={openNew} className="rounded-sm gap-1.5" data-testid="new-ad-btn">
          <Plus className="h-4 w-4" /> Nuovo banner
        </Button>
      </div>

      <div className="border border-border rounded-sm divide-y divide-border">
        {ads.length === 0 && <p className="p-6 text-muted-foreground text-sm">Nessun banner. I riquadri "Spazio pubblicitario disponibile" restano visibili come segnaposto.</p>}
        {ads.map((ad) => (
          <div key={ad.id} className="flex items-center gap-4 p-4" data-testid={`admin-ad-${ad.id}`}>
            <div className="w-24 h-14 bg-muted rounded-sm overflow-hidden shrink-0 flex items-center justify-center">
              {ad.image_url ? <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" /> : <span className="text-[10px] text-muted-foreground px-1 text-center">{ad.title}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={ad.active ? "default" : "secondary"} className="rounded-sm text-[10px]">{ad.active ? "Attivo" : "Disattivo"}</Badge>
                <span className="text-xs text-muted-foreground">{PLACEMENTS.find((p) => p.value === ad.placement)?.label}</span>
              </div>
              <p className="font-medium truncate">{ad.title}</p>
              {ad.sponsor && <p className="text-xs text-muted-foreground">Sponsor: {ad.sponsor}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch checked={ad.active} onCheckedChange={() => toggle(ad)} data-testid={`toggle-ad-${ad.id}`} />
              {ad.link_url && (
                <Button asChild variant="ghost" size="icon" className="rounded-sm">
                  <a href={ad.link_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a>
                </Button>
              )}
              <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => openEdit(ad)} data-testid={`edit-ad-${ad.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="rounded-sm text-accent" onClick={() => del(ad.id)} data-testid={`delete-ad-${ad.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">{editId ? "Modifica banner" : "Nuovo banner"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Titolo *</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-sm mt-1.5" data-testid="ad-title-input" placeholder="Es. Abbonati a..." />
            </div>
            <div>
              <Label>Sponsor</Label>
              <Input value={form.sponsor} onChange={(e) => set("sponsor", e.target.value)} className="rounded-sm mt-1.5" data-testid="ad-sponsor-input" placeholder="Nome dell'inserzionista" />
            </div>
            <div>
              <Label>URL immagine (banner)</Label>
              <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="rounded-sm mt-1.5" data-testid="ad-image-input" placeholder="https://..." />
            </div>
            <div>
              <Label>Link di destinazione</Label>
              <Input value={form.link_url} onChange={(e) => set("link_url", e.target.value)} className="rounded-sm mt-1.5" data-testid="ad-link-input" placeholder="https://..." />
            </div>
            <div>
              <Label>Posizione</Label>
              <Select value={form.placement} onValueChange={(v) => set("placement", v)}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="ad-placement-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLACEMENTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="ad-active-switch" />
              <Label>Attivo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="ad-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
